import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package,
  ChevronDown,
  ChevronUp,
  Truck,
  User,
  MapPin,
  CheckCircle2,
  Clock,
  CircleDot,
  Sparkles,
  BarChart3,
  ShoppingBag,
} from "lucide-react";

import type { RootState, AppDispatch } from "../../redux/store";
import {
  listenToClassifiedHostOrders,
  upsertClassifiedOrder,
} from "../../redux/classifiedOrdersSlice";
import { fetchAllUsers } from "../../redux/authSlice";
import { listenToHostClassified } from "../../redux/classifiedsSlice";

/* ─── PALETTE ─── */
const G = {
  black:     "#000000",
  ink:       "#080808",
  dark:      "#0f0f0f",
  panel:     "#141414",
  border:    "#1e1e1e",
  borderHi:  "#2a2a2a",
  offwhite:  "#ede8df",
  muted:     "#4a4a4a",
  dim:       "#282828",
  gold:      "#a8894a",
  goldLt:    "#bfa06a",
  goldDim:   "#4a3c20",
  goldGlow:  "rgba(168,137,74,0.07)",
  success:   "#2a5c3a",
  successDim:"#1a3025",
  warn:      "#6a4a10",
  warnDim:   "#2a1e08",
  blue:      "#2a2a5a",
  blueDim:   "#16162e",
};

/* ─── HELPERS ─── */
const fmt = (n?: number) =>
  !n || isNaN(n) ? "0.00" : Number(n).toFixed(2);

const getItemBadge = (status: any) => {
  const isFullyDelivered =
    status?.sellerDelivered && status?.orderDelivered && status?.buyerReceived;
  const isShipped = status?.sellerDelivered && !isFullyDelivered;

  if (isFullyDelivered)
    return { label: "Delivered", color: G.goldLt,  bg: G.successDim, dot: G.success };
  if (isShipped)
    return { label: "Shipped",   color: "#8a6a30", bg: G.warnDim,    dot: G.warn   };
  return   { label: "Pending",   color: "#4a4a7a", bg: G.blueDim,    dot: G.blue   };
};

/* ─── FONT LOADER ─── */
const GoogleFonts = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700;800&display=swap');
    * { box-sizing: border-box; }
    ::-webkit-scrollbar { width: 3px; }
    ::-webkit-scrollbar-thumb { background: #1e1e1e; }
  `}</style>
);

/* ─── HAIRLINE ─── */
const Hairline = ({ my = 14 }: { my?: number }) => (
  <div style={{
    margin: `${my}px 0`,
    height: 1,
    background: `linear-gradient(90deg, transparent, ${G.goldDim} 40%, ${G.goldDim} 60%, transparent)`,
  }} />
);

/* ─── STAT CHIP ─── */
function StatChip({ icon: Icon, label, value }: {
  icon: React.ElementType; label: string; value: string | number;
}) {
  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 4,
      padding: "12px 10px",
      background: G.ink,
      border: `1px solid ${G.border}`,
      flex: 1,
    }}>
      <Icon size={11} color={G.goldDim} strokeWidth={1.5} />
      <span style={{ fontSize: 9, color: G.muted, letterSpacing: 1.2, textTransform: "uppercase" as const }}>
        {label}
      </span>
      <span style={{ fontSize: 13, fontWeight: 700, color: G.offwhite, fontFamily: "monospace" }}>
        {value}
      </span>
    </div>
  );
}

/* ─── ACTION BUTTON ─── */
function ActionBtn({
  label, icon: Icon, onClick, variant = "ghost",
}: {
  label: string;
  icon: React.ElementType;
  onClick: () => void;
  variant?: "gold" | "ghost" | "dim";
}) {
  const [hover, setHover] = useState(false);
  const styles: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "5px 12px",
    fontSize: 10,
    letterSpacing: 1.2,
    fontWeight: 700,
    textTransform: "uppercase" as const,
    fontFamily: "'Montserrat', sans-serif",
    cursor: "pointer",
    transition: "all 0.2s",
    border: `1px solid ${
      variant === "gold"  ? G.goldDim :
      variant === "ghost" ? G.border  :
      G.dim
    }`,
    background: hover
      ? variant === "gold" ? G.goldGlow : "rgba(255,255,255,0.03)"
      : "transparent",
    color:
      variant === "gold"  ? G.gold  :
      variant === "ghost" ? G.muted :
      G.dim,
  };
  return (
    <button
      style={styles}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Icon size={10} strokeWidth={2} />
      {label}
    </button>
  );
}

/* ─── PRODUCT ROW ─── */
function ProductRow({
  item,
  listingsMap,
  hostId,
  orderId,
  onMarkShipped,
  onNavigate,
}: {
  item: any;
  listingsMap: Record<string, any>;
  hostId: string;
  orderId: string;
  onMarkShipped: (item: any) => void;
  onNavigate: (path: string) => void;
}) {
  const prod   = listingsMap[item.listingId] || listingsMap[item.id] || {};
  const vImg   = prod?.variants?.[item.colorId]?.imageUrl;
  const img    = vImg || prod?.photos?.[0] || item.image || "/default-product.jpg";
  const status = item.deliveryStatus || {};
  const badge  = getItemBadge(status);

  const isFullyDelivered = status.sellerDelivered && status.orderDelivered && status.buyerReceived;
  const isShipped        = status.sellerDelivered && !isFullyDelivered;
  const isPending        = !status.sellerDelivered;

  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        padding: "12px 0",
        borderBottom: `1px solid ${G.border}`,
        cursor: "pointer",
      }}
      onClick={() => onNavigate(`/generalcommercedetails/${item.listingId}`)}
    >
      {/* Image */}
      <div style={{ width: 52, height: 52, flexShrink: 0, overflow: "hidden", position: "relative" }}>
        <img
          src={img}
          alt={prod?.name || item.name}
          style={{ width: "100%", height: "100%", objectFit: "cover", filter: "brightness(0.8) saturate(0.75)" }}
          onError={(e) => ((e.target as HTMLImageElement).src = "/default-product.jpg")}
        />
        <div style={{ position: "absolute", inset: 0, border: `1px solid ${G.border}` }} />
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
          <span style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 14,
            fontWeight: 700,
            color: G.offwhite,
            lineHeight: 1.2,
          }}>
            {prod?.name || item.name}
          </span>
          <span style={{ fontFamily: "monospace", fontSize: 13, color: G.goldLt, fontWeight: 600, opacity: 0.75, flexShrink: 0 }}>
            €{fmt(prod?.rate ?? item.rate)}
          </span>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 2, flexWrap: "wrap" as const }}>
          {item.size   && <span style={S.metaTag}>{item.size}</span>}
          {item.colour && <span style={S.metaTag}>{item.colour}</span>}
          {item.colorId && !item.colour && <span style={S.metaTag}>{item.colorId}</span>}
          <span style={S.metaTag}>×{item.qty}</span>
        </div>

        {/* Badge */}
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 6 }}>
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: badge.dot }} />
          <span style={{
            fontSize: 9,
            fontWeight: 700,
            color: badge.color,
            letterSpacing: 1.5,
            textTransform: "uppercase" as const,
          }}>
            {badge.label}
          </span>
        </div>

        {/* CTA */}
        <div style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
          {isPending && (
            <ActionBtn
              label="Mark Shipped"
              icon={Truck}
              variant="gold"
              onClick={() => onMarkShipped(item)}
            />
          )}
          {isShipped && (
            <div style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "5px 12px",
              fontSize: 10,
              letterSpacing: 1.2,
              fontWeight: 700,
              textTransform: "uppercase" as const,
              border: `1px solid ${G.goldDim}`,
              color: G.gold,
              opacity: 0.6,
            }}>
              <CheckCircle2 size={10} strokeWidth={2} />
              Shipped
            </div>
          )}
          {isFullyDelivered && (
            <div style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "5px 12px",
              fontSize: 10,
              letterSpacing: 1.2,
              fontWeight: 700,
              textTransform: "uppercase" as const,
              border: `1px solid ${G.goldDim}`,
              color: G.goldLt,
              opacity: 0.55,
            }}>
              <CheckCircle2 size={10} strokeWidth={2} />
              Delivered
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── ORDER CARD ─── */
function OrderCard({
  order,
  hostId,
  guestName,
  listingsMap,
  isOpen,
  onToggle,
  onMarkShipped,
  onNavigate,
}: {
  order: any;
  hostId: string;
  guestName: string;
  listingsMap: Record<string, any>;
  isOpen: boolean;
  onToggle: () => void;
  onMarkShipped: (item: any) => void;
  onNavigate: (path: string) => void;
}) {
  const hostItems = (order.items || []).filter((i: any) => i.hostId === hostId);
  const firstItem = hostItems[0];

  const prod  = listingsMap[firstItem?.listingId] || listingsMap[firstItem?.id] || {};
  const vImg  = prod?.variants?.[firstItem?.colorId]?.imageUrl;
  const photo = vImg || prod?.photos?.[0] || firstItem?.image || "/default-product.jpg";

  const hostProfit = order?.hostProfits?.[hostId]?.hostProfit ?? 0;
  const hostFee    = order?.hostProfits?.[hostId]?.hostsFee   ?? 0;

  const allShipped = hostItems.every((i: any) => i.deliveryStatus?.sellerDelivered);
  const allDone    = hostItems.every((i: any) =>
    i.deliveryStatus?.sellerDelivered &&
    i.deliveryStatus?.orderDelivered &&
    i.deliveryStatus?.buyerReceived
  );

  const statusColor = allDone ? G.gold : allShipped ? "#7a6030" : G.muted;
  const statusLabel = allDone ? "Delivered" : allShipped ? "Shipped" : "Pending";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.2, 0, 0.1, 1] }}
      style={{
        border: `1px solid ${isOpen ? G.goldDim : G.border}`,
        background: G.ink,
        overflow: "hidden",
        transition: "border-color 0.3s",
      }}
    >
      {isOpen && (
        <div style={{ height: 1, background: `linear-gradient(90deg, transparent, ${G.goldDim} 40%, ${G.goldDim} 60%, transparent)` }} />
      )}

      {/* HEADER */}
      <div style={{ display: "flex", gap: 0, cursor: "pointer" }} onClick={onToggle}>
        <div style={{ width: 80, height: 80, flexShrink: 0, overflow: "hidden", position: "relative" }}>
          <img
            src={photo}
            alt={prod?.name || firstItem?.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", filter: "brightness(0.75) saturate(0.7)" }}
            onError={(e) => ((e.target as HTMLImageElement).src = "/default-product.jpg")}
          />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, transparent 50%, rgba(8,8,8,0.7))" }} />
        </div>

        <div style={{ flex: 1, padding: "14px 16px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15, fontWeight: 700, color: G.offwhite, lineHeight: 1.2 }}>
                {prod?.name || firstItem?.name || "Classified Order"}
                {hostItems.length > 1 && (
                  <span style={{ fontSize: 11, color: G.muted, fontFamily: "'Montserrat', sans-serif", fontWeight: 400, marginLeft: 6 }}>
                    +{hostItems.length - 1}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 9, color: G.muted, letterSpacing: 1.5, textTransform: "uppercase" as const, marginTop: 3 }}>
                {order.orderId || order.id?.slice(0, 12)}
              </div>
            </div>
            {isOpen
              ? <ChevronUp   size={14} color={G.gold}  strokeWidth={1.5} />
              : <ChevronDown size={14} color={G.muted} strokeWidth={1.5} />
            }
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 4, height: 4, borderRadius: "50%", background: statusColor, opacity: 0.8 }} />
            <span style={{ fontSize: 9, fontWeight: 700, color: statusColor, letterSpacing: 2, textTransform: "uppercase" as const }}>
              {statusLabel}
            </span>
            <span style={{ fontSize: 9, color: G.muted, marginLeft: 8 }}>{guestName}</span>
          </div>
        </div>
      </div>

      {/* EXPANDED BODY */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
            style={{ overflow: "hidden" }}
          >
            <div style={{ padding: "16px 20px", background: G.dark }}>

              {/* ORDER INFO */}
              <div style={S.sectionLabel}>Order Info</div>
              <div style={S.infoRow}>
                <span style={S.infoKey}>
                  <User size={10} strokeWidth={1.5} style={{ display: "inline", marginRight: 5, verticalAlign: "middle" }} />
                  Guest
                </span>
                <span style={S.infoVal}>{guestName}</span>
              </div>
              <div style={S.infoRow}>
                <span style={S.infoKey}>
                  <CircleDot size={10} strokeWidth={1.5} style={{ display: "inline", marginRight: 5, verticalAlign: "middle" }} />
                  Status
                </span>
                <span style={{ ...S.infoVal, color: statusColor }}>{statusLabel}</span>
              </div>
              <div style={S.infoRow}>
                <span style={S.infoKey}>
                  <Clock size={10} strokeWidth={1.5} style={{ display: "inline", marginRight: 5, verticalAlign: "middle" }} />
                  Placed
                </span>
                <span style={S.infoVal}>
                  {order.createdAt
                    ? new Date(order.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                    : "—"}
                </span>
              </div>

              <Hairline my={14} />

              {/* PRODUCTS */}
              <div style={S.sectionLabel}>Items</div>
              {hostItems.map((item: any, idx: number) => (
                <ProductRow
                  key={(item.listingId || item.id) + idx}
                  item={item}
                  listingsMap={listingsMap}
                  hostId={hostId}
                  orderId={order.id}
                  onMarkShipped={onMarkShipped}
                  onNavigate={onNavigate}
                />
              ))}

              {/* ADDRESS */}
              <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" as const }}>
                <ActionBtn
                  label="Delivery Address"
                  icon={MapPin}
                  variant="ghost"
                  onClick={() => onNavigate(`/shippingaddress?guestId=${order.guestId}`)}
                />
              </div>

              <Hairline my={14} />

              {/* FINANCIALS */}
              <div style={S.sectionLabel}>Earnings</div>
              <div style={S.infoRow}>
                <span style={S.infoKey}>Service Fee</span>
                <span style={S.infoVal}>€{fmt(hostFee)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 14, fontWeight: 700, color: G.offwhite, letterSpacing: 1.5, textTransform: "uppercase" as const }}>
                  Your Profit
                </span>
                <span style={{ fontFamily: "monospace", fontSize: 17, fontWeight: 700, color: G.gold, opacity: 0.85 }}>
                  €{fmt(hostProfit)}
                </span>
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */

export default function MetaCommerceHostOrders() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const user   = useSelector((s: RootState) => s.auth.user);
  const hostId = user?.userId || user?.uid || "";

  const orders   = useSelector((s: RootState) => s.classifiedOrders.orders);
  const listings = useSelector((s: RootState) => s.classifieds.listings);
  const users    = useSelector((s: RootState) => s.auth.allUsers);

  const [expandedId, setExpandedId] = useState<string | null>(null);

  /* ── Load ── */
  useEffect(() => {
    if (!hostId) return;
    dispatch(listenToClassifiedHostOrders(hostId));
    dispatch(listenToHostClassified({ hostId }));
    dispatch(fetchAllUsers());
  }, [hostId, dispatch]);

  /* ── Listings map ── */
  const listingsMap = useMemo(() => {
    const m: Record<string, any> = {};
    listings.forEach((l: any) => (m[l.listingId] = l));
    return m;
  }, [listings]);

  /* ── Guest lookup ── */
  const getGuestName = (id: string) => {
    const u = users.find((u: any) => u.userId === id || u.uid === id);
    return u?.firstName || u?.displayName || u?.email || "Guest";
  };

  /* ── Active orders only ── */
  const activeOrders = useMemo(
    () => orders
      .filter((o: any) => !o.isDelivered)
      .sort((a: any, b: any) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()),
    [orders]
  );

  /* ── Summary stats ── */
  const totalProfit  = orders.reduce((s: number, o: any) => s + (o?.hostProfits?.[hostId]?.hostProfit ?? 0), 0);
  const pendingCount = activeOrders.filter((o: any) =>
    !(o.items || []).some((i: any) => i.deliveryStatus?.sellerDelivered)
  ).length;
  const shippedCount = activeOrders.filter((o: any) =>
    (o.items || []).some((i: any) => i.deliveryStatus?.sellerDelivered && !i.deliveryStatus?.orderDelivered)
  ).length;

  /* ── Mark shipped ── */
  const handleMarkShipped = (order: any, item: any) => {
    dispatch(
      upsertClassifiedOrder({
        paymentId: order.id,
        hostId,
        listingId: item.listingId,
        deliveryStatus: { sellerDelivered: true },
      })
    );
  };

  /* ── Empty state ── */
  if (activeOrders.length === 0) {
    return (
      <div style={S.page}>
        <GoogleFonts />
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{ textAlign: "center", maxWidth: 360 }}
        >
          <Package size={36} color={G.goldDim} strokeWidth={1} style={{ margin: "0 auto 20px", display: "block" }} />
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, color: G.offwhite, fontWeight: 400, letterSpacing: 3, marginBottom: 10 }}>
            No Active Orders
          </h3>
          <p style={{ color: G.muted, fontSize: 12, letterSpacing: 0.5, lineHeight: 1.6 }}>
            Orders from your listings will appear here.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <GoogleFonts />

      <div style={S.inner}>

        {/* ── PAGE HEADER ── */}
        <div style={S.pageHeader}>
          <div style={S.headerRule}>
            <div style={S.ruleLine} />
            <Sparkles size={11} color={G.goldDim} strokeWidth={1.5} />
            <div style={S.ruleLine} />
          </div>
          <h1 style={S.pageTitle}>Order Management</h1>
        </div>

        {/* ── SUMMARY STATS ── */}
        <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
          <StatChip icon={ShoppingBag} label="Active"   value={activeOrders.length}     />
          <StatChip icon={Clock}       label="Pending"  value={pendingCount}             />
          <StatChip icon={Truck}       label="Shipped"  value={shippedCount}             />
          <StatChip icon={BarChart3}   label="Earnings" value={`€${fmt(totalProfit)}`}  />
        </div>

        {/* ── ORDER CARDS ── */}
        <div style={S.cardGrid}>
          {activeOrders.map((order: any) => (
            <OrderCard
              key={order.id}
              order={order}
              hostId={hostId}
              guestName={getGuestName(order.guestId)}
              listingsMap={listingsMap}
              isOpen={expandedId === order.id}
              onToggle={() => setExpandedId((p) => p === order.id ? null : order.id)}
              onMarkShipped={(item) => handleMarkShipped(order, item)}
              onNavigate={navigate}
            />
          ))}
        </div>

      </div>
    </div>
  );
}

/* ─── STYLES ─── */
const S: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: G.black,
    paddingTop: 80,
    paddingBottom: 60,
    fontFamily: "'Montserrat', sans-serif",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  inner: {
    maxWidth: 900,
    margin: "0 auto",
    padding: "0 16px",
    width: "100%",
  },
  pageHeader: {
    textAlign: "center",
    marginBottom: 28,
  },
  headerRule: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    marginBottom: 10,
  },
  ruleLine: {
    flex: 1,
    height: 1,
    background: `linear-gradient(90deg, transparent, ${G.goldDim})`,
    maxWidth: 80,
  },
  pageTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 24,
    fontWeight: 400,
    color: G.offwhite,
    letterSpacing: 5,
    textTransform: "uppercase",
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: 10,
  },
  sectionLabel: {
    fontSize: 9,
    color: G.muted,
    letterSpacing: 2.5,
    textTransform: "uppercase" as const,
    marginBottom: 10,
    fontWeight: 700,
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "6px 0",
    borderBottom: `1px solid ${G.border}`,
  },
  infoKey: {
    fontSize: 11,
    color: G.muted,
    letterSpacing: 0.3,
  },
  infoVal: {
    fontSize: 11,
    color: G.offwhite,
    fontWeight: 600,
  },
  metaTag: {
    fontSize: 9,
    color: G.muted,
    letterSpacing: 1,
    textTransform: "uppercase" as const,
  },
};